import React from 'react';
import '../../styles/landing.css';
import {
  IconBrain, IconCode, IconList, IconBook, IconCards, IconInterview, IconGraph,
  IconArrowRight, IconSpark, IconTarget, IconClock, IconCheck,
} from './icons';

/*
 * NanoLanding — the app home / hero (shown after login, full-bleed). Light,
 * modern, and cohesive with the rest of DataVerse.ai: a headline + CTAs, a live
 * "AI mind" orbit visual, a grid of the six modes, and a value strip.
 */
const CAPS = [
  { id: 'dsa', label: 'DSA', Icon: IconCode, accent: '#818cf8', desc: 'Solve coding problems with an AI tutor & judge.' },
  { id: 'mcq', label: 'MCQ', Icon: IconList, accent: '#f59e0b', desc: 'Topic quizzes that track your accuracy.' },
  { id: 'theory', label: 'Theory', Icon: IconBook, accent: '#0ea5e9', desc: 'Concepts explained with worked examples.' },
  { id: 'flashcards', label: 'Flashcards', Icon: IconCards, accent: '#a855f7', desc: 'Revise fast with spaced-repetition decks.' },
  { id: 'interviewer', label: 'AI Interviewer', Icon: IconInterview, accent: '#ef4444', desc: 'Live voice mock interviews with feedback.' },
  { id: 'hub', label: 'Knowledge Hub', Icon: IconGraph, accent: '#10b981', desc: 'Everything you can learn, one connected map.' },
];

const WHY = [
  { Icon: IconTarget, title: 'Adaptive', text: 'The engine picks your next question from what you’ve mastered — and where you’re weak.' },
  { Icon: IconClock, title: 'Instant feedback', text: 'Hints, explanations, complexity and debugging on your own code, in real time.' },
  { Icon: IconCheck, title: 'Real progress', text: 'Every attempt updates your memory across topics so practice actually compounds.' },
];

// central mind + orbiting nodes (radial layout)
const R = 128;
const ORBIT = CAPS.map((c, i) => {
  const ang = (-90 + i * (360 / CAPS.length)) * (Math.PI / 180);
  return { ...c, x: 50 + (R / 3.2) * Math.cos(ang), y: 50 + (R / 3.2) * Math.sin(ang) };
});

const NanoLanding = ({ onStart, onSelectMode, onOpenHub }) => {
  const activate = (c) => (c.id === 'hub' ? onOpenHub() : onSelectMode(c.id));
  const scrollToModes = () => document.getElementById('lp-modes')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="lp">
      <div className="lp-glow" />

      {/* Nav */}
      <nav className="lp-nav">
        <div className="lp-brand">
          <span className="lp-brand-logo"><IconSpark size={17} /></span>
          <span className="lp-brand-name">DataVerse.ai</span>
        </div>
        <button className="lp-btn primary sm" onClick={onStart} type="button">
          Get Started <IconArrowRight size={15} />
        </button>
      </nav>

      {/* Hero */}
      <header className="lp-hero">
        <div className="lp-hero-copy">
          <span className="lp-badge"><IconSpark size={13} /> Your all-in-one AI learning companion</span>
          <h1>One mind.<br />Every way to learn.</h1>
          <p>Solve, quiz, revise, and interview — all guided by a single adaptive AI that
            remembers your strengths, weak spots, and what to practice next.</p>
          <div className="lp-cta-row">
            <button className="lp-btn primary" onClick={onStart} type="button">
              Start learning <IconArrowRight size={16} />
            </button>
            <button className="lp-btn ghost" onClick={scrollToModes} type="button">Explore modes</button>
          </div>
          <div className="lp-stats">
            <span><b>2,800+</b> problems</span><i />
            <span><b>6</b> learning modes</span><i />
            <span><b>Adaptive</b> by design</span>
          </div>
        </div>

        {/* Orbit visual */}
        <div className="lp-orbit" role="img" aria-label="DataVerse.ai capabilities orbiting one AI mind">
          <span className="lp-ring r1" />
          <span className="lp-ring r2" />
          <svg className="lp-edges" viewBox="0 0 100 100" preserveAspectRatio="none">
            {ORBIT.map((c) => <line key={c.id} x1="50" y1="50" x2={c.x} y2={c.y} stroke={c.accent} strokeOpacity="0.28" strokeWidth="0.5" />)}
          </svg>
          <button className="lp-core" onClick={onStart} type="button" title="Start a session">
            <IconBrain size={38} />
          </button>
          {ORBIT.map((c) => (
            <button
              key={c.id}
              className="lp-node"
              style={{ left: `${c.x}%`, top: `${c.y}%`, '--ac': c.accent }}
              onClick={() => activate(c)}
              title={c.label}
              type="button"
            >
              <c.Icon size={17} />
            </button>
          ))}
        </div>
      </header>

      {/* Modes */}
      <section className="lp-modes" id="lp-modes">
        <h2>Six ways to master anything</h2>
        <p className="lp-sec-sub">Pick a mode to jump straight in.</p>
        <div className="lp-grid">
          {CAPS.map((c) => (
            <button key={c.id} className="lp-card" style={{ '--ac': c.accent }} onClick={() => activate(c)} type="button">
              <span className="lp-card-ico"><c.Icon size={20} /></span>
              <div className="lp-card-body">
                <div className="lp-card-title">{c.label}</div>
                <div className="lp-card-desc">{c.desc}</div>
              </div>
              <IconArrowRight size={16} className="lp-card-arrow" />
            </button>
          ))}
        </div>
      </section>

      {/* Why */}
      <section className="lp-why">
        {WHY.map((w) => (
          <div key={w.title} className="lp-why-card">
            <span className="lp-why-ico"><w.Icon size={18} /></span>
            <div className="lp-why-title">{w.title}</div>
            <div className="lp-why-text">{w.text}</div>
          </div>
        ))}
      </section>

      {/* Footer CTA */}
      <footer className="lp-foot">
        <div className="lp-foot-cta">
          <div>
            <h3>Ready to learn smarter?</h3>
            <p>Start a session and let the AI take it from there.</p>
          </div>
          <button className="lp-btn primary" onClick={onStart} type="button">Start learning <IconArrowRight size={16} /></button>
        </div>
        <div className="lp-foot-legal">© DataVerse.ai · Learn faster, interview smarter.</div>
      </footer>
    </div>
  );
};

export default NanoLanding;
